import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  isDevMode
} from '@angular/core';
import { Router } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import { StoreEndpointsConstant } from '@constants/store-endpoints.constant';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { SessionService } from '@services/session/session.service';
import { TranslationService } from '@services/translation/translation.service';
import { ViewService } from 'tangoe-design-system';

@Component({
  selector: 'ts-generic-error',
  templateUrl: './generic-error.component.html',
  imports: [TranslationPipe]
})
export class GenericErrorComponent implements OnChanges, OnInit, OnDestroy {
  @Input('error') error?: string | undefined = undefined;
  @HostBinding('class') class: string = 'd-contents';
  title: string = '';
  main: string = '';
  main2: string = '';
  hint: string = '';
  error_code: number | undefined = undefined;
  return_to_dashboard: boolean = false;

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private router: Router,
    private session_svc: SessionService,
    private translation_svc: TranslationService,
    private view_svc: ViewService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.main2 = this.translation_svc.translate('apologize_for_inconvenience');
    this.hint = this.translation_svc.translate('assistance_text');
    this.error_code = undefined;
    this.return_to_dashboard = false;

    switch (this.error) {
      case 'access':
        this.title = this.translation_svc.translate('access_error_title');
        this.main = this.translation_svc.translate('access_error_main');
        this.view_svc.setTitle(this.title);
        break;

      case 'backend':
        this.title = this.translation_svc.translate('backend_error_title');
        this.main = this.translation_svc.translate('backend_error_main');
        this.main2 = this.translation_svc.translate('backend_error_main2');
        this.hint = this.translation_svc.translate('backend_error_hint');
        this.error_code = 500;
        this.return_to_dashboard = true;
        break;

      case 'forbidden':
        this.title = this.translation_svc.translate('forbidden_error_title');
        this.main = this.translation_svc.translate('forbidden_error_main');
        this.main2 = this.translation_svc.translate('forbidden_error_main2');
        this.error_code = 403;
        break;

      case 'request':
        this.title = this.translation_svc.translate('request_error_title');
        this.main = this.translation_svc.translate('request_error_main');
        this.main2 = this.translation_svc.translate('resolving_error_txt');
        this.error_code = 500;
        break;

      case 'resource':
        this.title = this.translation_svc.translate('resource_error_title');
        this.main = this.translation_svc.translate('resource_error_main');
        this.error_code = 404;
        this.view_svc.setTitle(this.title);
        break;

      case 'service':
        this.title = this.translation_svc.translate('service_error_title');
        this.main = this.translation_svc.translate('service_error_main');
        this.main2 = this.translation_svc.translate('resolving_error_txt');
        this.view_svc.setTitle(this.title);
        break;

      case 'unauthorized':
        if (isDevMode()) {
          this.title = this.translation_svc.translate('unauthorized_error_title');
          this.main = this.translation_svc.translate('unauthorized_error_main');
          this.main2 = this.translation_svc.translate('unauthorized_error_main2');
          this.error_code = 401;
          this.view_svc.setTitle(this.title);
        } else window.location.href = StoreEndpointsConstant.DIRECT_LOGIN;

        break;

      case 'page-not-found':
      default:
        this.title = this.translation_svc.translate('page_not_found');
        this.main = this.translation_svc.translate('page_not_found_txt');
        this.error_code = 404;
        this.view_svc.setTitle(this.title);
        break;
    }
  }

  ngOnInit(): void {
    this.view_svc.addClassWithAnimationFrame(this.view_svc.scrollableElement(), 'bg-body');
  }

  ngOnDestroy(): void {
    this.view_svc.removeClassWithAnimationFrame(this.view_svc.scrollableElement(), 'bg-body');
  }

  isUserLoggedIn(): boolean {
    return this.session_svc.hasUser();
  }

  showNavigateToLoginPageButton(): boolean {
    return !this.isUserLoggedIn() && this.view_svc.isLocalhost();
  }

  showReturnToDashboardButton(): boolean {
    return this.return_to_dashboard && this.isUserLoggedIn();
  }

  navigateToDashboard(): void {
    this.activity_manager_svc.cancelCurrentActivity();
  }

  navigateToLogin(): void {
    this.router.navigate([PageUrls.LOGIN]);
  }
}
